import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { TableBody, TableCell, TableRow } from '@mui/material';
import { AmountFormat, IndicatorIcon, LocalDateFormat, PayerLoadingOverlay, Table } from '../..';
import { OldPaymentStatusChip } from './OldPaymentStatusChip';

const InvoiceCard = ({ invoice, isFetching = false, isInternalUI = false }) => {
  const { t } = useTranslation();
  const [cachedInvoice, setCachedInvoice] = useState(null);

  useEffect(() => {
    if (invoice || !isFetching) {
      setCachedInvoice(invoice);
    }
  }, [invoice, isFetching]);

  const {
    invoiceNumber,
    merchantId,
    externalInvoiceNumber,
    invoiceDate,
    dueDate,
    amountToPay,
    currencyCode,
    type,
    deliveryType,
    paymentStatus,
    balance,
    creditedAmount,
    invoiceCustomer,
    reminders,
    sentToDebtCollection,
  } = cachedInvoice ?? {};

  const sentReminder = !isEmpty(reminders);

  return (
    <PayerLoadingOverlay active={isFetching}>
      {(cachedInvoice || isFetching) && (
        <Card>
          <CardHeader>
            <Row>
              <Col xs="7" tag="h5">
                {t('components.cards.invoice.title', { invoiceNumber })}
              </Col>
              <Col xs="5" className="text-right">
                {paymentStatus && <OldPaymentStatusChip status={paymentStatus} />}
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Table style={{ ...(!cachedInvoice && { visibility: 'hidden' }) }}>
              <TableBody>
                {isInternalUI && (
                  <TableRow>
                    <TableCell>Merchant</TableCell>
                    <TableCell>{merchantId}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>{t('invoices.fields.externalInvoiceNumber')}</TableCell>
                  <TableCell>{externalInvoiceNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.fields.customerNumber')}</TableCell>
                  <TableCell>{invoiceCustomer?.customerNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.fields.company')}</TableCell>
                  <TableCell>{invoiceCustomer?.address?.companyName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.fields.invoiceDate')}</TableCell>
                  <TableCell>{invoiceDate && <LocalDateFormat date={invoiceDate} />}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.fields.dueDate')}</TableCell>
                  <TableCell>{dueDate && <LocalDateFormat date={dueDate} />}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('enums.invoiceType.name')}</TableCell>
                  <TableCell>{type}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.fields.deliveryType')}</TableCell>
                  <TableCell>{deliveryType}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('amounts.originalAmount')}</TableCell>
                  <TableCell className="text-right">
                    {amountToPay && <AmountFormat amount={amountToPay} currencyCode={currencyCode} />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('amounts.creditedAmount')}</TableCell>
                  <TableCell className="text-right">
                    {creditedAmount && <AmountFormat amount={creditedAmount} currencyCode={currencyCode} />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('amounts.balance')}</TableCell>
                  <TableCell className="text-right">
                    {balance && <AmountFormat amount={balance} currencyCode={currencyCode} />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.sentReminder')}</TableCell>
                  <TableCell>{sentReminder && <IndicatorIcon isEnabled />}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('invoices.sentForDebtCollection')}</TableCell>
                  <TableCell>{sentToDebtCollection && <IndicatorIcon isEnabled />}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      )}
    </PayerLoadingOverlay>
  );
};

InvoiceCard.propTypes = {
  invoice: PropTypes.object,
  isFetching: PropTypes.bool,
  isInternalUI: PropTypes.bool,
};

export { InvoiceCard };
